/**
 * useSymfoniaPeriodDateDraft.ts — stan roboczy pola „Data okresu" faktury (miesiąc księgowy
 * w Symfonii FK, `YYYY-MM-01` albo `null`) na stronie dokumentu.
 *
 * DLACZEGO NA STRONIE, A NIE W EDYTORZE: `Tabs` renderuje tylko aktywny panel, więc
 * `DecreeLinesEditor` odmontowuje się przy przełączeniu zakładki. Stan trzymany w edytorze
 * (ref „wybrano ręcznie", podpowiedź z efektu) ginął razem z nim: po powrocie na zakładkę
 * ręczny wybór był nadpisywany, a zmiana daty dokumentu na zakładce „Dane" nie docierała do
 * odmontowanego edytora (zapisywał się nieaktualny miesiąc).
 *
 * Reguły (ustalone biznesowo):
 *  - Podpowiedź = miesiąc daty dokumentu. Włącza się DOPIERO po aktywacji (`activate()` —
 *    edytor woła ją raz po zamontowaniu, gdy pole jest widoczne), więc samo edytowanie
 *    zakładki „Dane" bez zaglądania w Dekretację nie zapisuje żadnego okresu.
 *  - Podpowiedź podąża za datą dokumentu (także przy odmontowanym edytorze), dopóki wartość
 *    nie jest zapisana na serwerze i użytkownik nie wybrał/nie wyczyścił miesiąca ręcznie.
 *    Data dokumentu pusta/niepoprawna albo z rocznikiem spoza zakresu FK (literówka typu
 *    `0026-…`) → podpowiedź znika (`null`), zapis faktury nie jest blokowany.
 *  - Wartość zapisana na serwerze nigdy nie jest podmieniana automatycznie.
 *  - Ręczny wybór lub wyczyszczenie (`change`) jest lepki — wygrywa z podpowiedzią.
 *  - `dirty` = wartość robocza różni się od zapisanej: tylko wtedy pole idzie w PATCH
 *    (łącznie z `null` = wyczyszczenie zapisanego miesiąca).
 *  - `reset()` — po zapisie i przy anulowaniu edycji: wraca stan „czytaj z serwera".
 *
 * Wartość robocza jest WYLICZANA (z `saved`, `documentDate`, `active`, ręcznego wyboru),
 * nie kopiowana efektem — dzięki temu nie ma okna, w którym draft zostaje stary względem
 * danych z serwera lub z formularza.
 */

import { useCallback, useState } from 'react';

import { monthStartOf } from '@/lib/documents/period-date';

export interface UseSymfoniaPeriodDateDraftArgs {
  /** Data okresu zapisana na dokumencie (z GET); `null`/brak = niezapisana. */
  saved: string | null | undefined;
  /** Bieżąca data dokumentu z formularza (`YYYY-MM-DD`) — źródło podpowiedzi. */
  documentDate: string | null | undefined;
}

export interface SymfoniaPeriodDateDraft {
  /** Bieżąca wartość robocza (`YYYY-MM-01` albo `null`) — to widzi pole i to idzie w PATCH. */
  draft: string | null;
  /** Wartość robocza różni się od zapisanej → dołącz `symfoniaPeriodDate` do payloadu. */
  dirty: boolean;
  /** Użytkownik wybrał lub wyczyścił miesiąc ręcznie (podpowiedź wyłączona). */
  touched: boolean;
  /** Pole Daty okresu było pokazane (edytor zamontowany) — podpowiedź jest włączona. */
  active: boolean;
  /** Włącza podpowiedź; idempotentne. Woła edytor po zamontowaniu. */
  activate: () => void;
  /** Ręczna zmiana z edytora (miesiąc albo `null` = wyczyszczenie). */
  change: (next: string | null) => void;
  /** Powrót do „czytaj z serwera": po zapisie i przy anulowaniu edycji. */
  reset: () => void;
}

export function useSymfoniaPeriodDateDraft({
  saved,
  documentDate,
}: UseSymfoniaPeriodDateDraftArgs): SymfoniaPeriodDateDraft {
  const savedValue = saved ?? null;
  const [active, setActive] = useState(false);
  // Opakowanie w obiekt odróżnia „nie ruszano" (`null`) od „ręcznie wyczyszczono"
  // (`{ value: null }`).
  const [manual, setManual] = useState<{ value: string | null } | null>(null);

  const draft: string | null =
    manual !== null
      ? manual.value
      : active && savedValue === null
        ? monthStartOf(documentDate)
        : savedValue;

  const activate = useCallback(() => setActive(true), []);
  const change = useCallback((next: string | null) => setManual({ value: next }), []);
  const reset = useCallback(() => {
    setManual(null);
    setActive(false);
  }, []);

  return {
    draft,
    dirty: draft !== savedValue,
    touched: manual !== null,
    active,
    activate,
    change,
    reset,
  };
}
